import { useCallback, useEffect, useRef, useState } from 'react';
import type { AccountCreditsDto } from '@primetime/shared/apiContracts';
import { ApiError, api } from '../../services/apiClient';
import { onAccountStateSync } from './accountStateSync';

export type AccountCreditStatus = 'loading' | 'ready' | 'error' | 'unauthenticated';

export type AccountCreditState = {
  status: AccountCreditStatus;
  data: AccountCreditsDto | null;
  error: string | null;
};

export const ACCOUNT_CREDITS_ERROR_MESSAGE = 'Credit balance is temporarily unavailable.';

function isAccountCreditsDto(value: unknown): value is AccountCreditsDto {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AccountCreditsDto>;
  return (
    typeof candidate.availableCredits === 'number' &&
    Number.isFinite(candidate.availableCredits) &&
    candidate.availableCredits >= 0 &&
    Array.isArray(candidate.creditBuckets) &&
    candidate.creditBuckets.every((bucket) => (
      bucket &&
      typeof bucket.id === 'string' &&
      typeof bucket.creditsRemaining === 'number' &&
      Number.isFinite(bucket.creditsRemaining) &&
      bucket.creditsRemaining >= 0 &&
      typeof bucket.expiresAt === 'string'
    ))
  );
}

function accountCreditsError(error: unknown) {
  if (error instanceof ApiError && error.status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  return ACCOUNT_CREDITS_ERROR_MESSAGE;
}

export function useAccountCredits(userId: string | null | undefined, authLoading: boolean) {
  const [creditState, setCreditState] = useState<AccountCreditState>({
    status: 'loading',
    data: null,
    error: null
  });
  const [blockedVersionIds, setBlockedVersionIds] = useState<Set<string>>(() => new Set());
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const refreshCredits = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    abortControllerRef.current?.abort();

    if (authLoading) {
      setCreditState({ status: 'loading', data: null, error: null });
      return null;
    }

    if (!userId) {
      setCreditState({ status: 'unauthenticated', data: null, error: null });
      return null;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setCreditState((current) => ({
      status: 'loading',
      data: current.status === 'ready' ? current.data : null,
      error: null
    }));

    try {
      const result = await api<AccountCreditsDto>('/api/account/credits', {
        signal: controller.signal
      });
      if (!isAccountCreditsDto(result)) {
        throw new Error('Invalid account credit response');
      }
      if (requestId !== requestIdRef.current || controller.signal.aborted) return null;
      setCreditState({ status: 'ready', data: result, error: null });
      return result;
    } catch (error) {
      if (controller.signal.aborted || requestId !== requestIdRef.current) return null;
      setCreditState({ status: 'error', data: null, error: accountCreditsError(error) });
      return null;
    }
  }, [authLoading, userId]);

  useEffect(() => {
    setBlockedVersionIds(new Set());
    void refreshCredits();
  }, [refreshCredits]);

  useEffect(() => {
    return onAccountStateSync(({ updatedCredits }) => {
      if (!userId || !isAccountCreditsDto(updatedCredits)) return;
      setCreditState({ status: 'ready', data: updatedCredits, error: null });
    });
  }, [userId]);

  useEffect(() => () => {
    requestIdRef.current += 1;
    abortControllerRef.current?.abort();
  }, []);

  const creditStateReady = creditState.status === 'ready' && Boolean(creditState.data);

  const requiresSubscribe = useCallback((versionId: string, creditCost: number) => {
    if (!userId || !creditStateReady || !creditState.data) return false;
    if (blockedVersionIds.has(versionId)) return true;
    return creditState.data.availableCredits < creditCost;
  }, [blockedVersionIds, creditState.data, creditStateReady, userId]);

  const markInsufficientCredits = useCallback(async (versionId: string, creditCost: number) => {
    setBlockedVersionIds((current) => new Set(current).add(versionId));
    const latest = await refreshCredits();
    if (latest && latest.availableCredits >= creditCost) {
      setBlockedVersionIds((current) => {
        const next = new Set(current);
        next.delete(versionId);
        return next;
      });
    }
    return latest;
  }, [refreshCredits]);

  return {
    availableCredits: creditState.data?.availableCredits ?? null,
    creditState,
    creditStateReady,
    markInsufficientCredits,
    refreshCredits,
    requiresSubscribe
  };
}
